import {
  BriefcaseIcon,
  EyeOffIcon,
  FileTextIcon,
  FolderOpenIcon,
  GraduationCapIcon,
  MessageSquareIcon,
  PresentationIcon,
  SearchIcon,
  ShuffleIcon,
  SparklesIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react'

export const PRICE = '$27'
export const BRAND = 'Makeup Instructor Ready'

export const systemFlow = ['Know', 'Teach', 'Plan', 'Practice', 'Assess', 'Manage']

/** Section 2 — the problems. */
export const problems = [
  { icon: FileTextIcon, text: 'Planning lessons from zero' },
  { icon: SearchIcon, text: 'Searching for activities before every class' },
  { icon: EyeOffIcon, text: 'Explaining techniques without visual resources' },
  { icon: ShuffleIcon, text: 'Students practicing without a clear progression' },
  { icon: MessageSquareIcon, text: 'Giving feedback without a consistent assessment system' },
  { icon: FolderOpenIcon, text: 'Managing classes, records and materials across different documents' },
]

/** Section 3 — before and after. */
export const beforeSystem = [
  'Scattered notes',
  'Improvised lessons',
  'Random exercises',
  'Subjective feedback',
  'No clear student progression',
  'Hours preparing materials',
  'Disconnected documents',
]

export const afterSystem = [
  'Structured curriculum',
  'Ready-to-teach frameworks',
  'Guided student practice',
  'Professional assessment',
  'Progress tracking',
  'Visual teaching resources',
  'Organized classroom systems',
]

/** Section 5 — the six core volumes. Crops point at each cover inside the mockup. */
export const volumes = [
  {
    number: '01',
    stage: 'Know',
    title: 'Makeup Education Foundations',
    subtitle: 'The Essential Knowledge & Teaching Reference for Makeup Instructors',
    contents:
      'Professional terminology, products, tools, skin preparation, foundation matching, undertones, color theory, facial analysis, eye shapes, brows, lips, hygiene and essential teaching references.',
    benefit: 'Build the knowledge foundation behind confident professional instruction.',
    crop: { x: 92, y: 130, w: 212, h: 330 },
  },
  {
    number: '02',
    stage: 'Teach',
    title: 'Professional Makeup Techniques',
    subtitle: 'The Step-by-Step Technique Teaching Library',
    contents:
      'Professional technique breakdowns covering complexion, contour, brows, eyeshadow, blending, eyeliner, lashes, lips and complete looks.',
    benefit: 'Demonstrate techniques systematically instead of improvising explanations.',
    crop: { x: 318, y: 138, w: 212, h: 330 },
  },
  {
    number: '03',
    stage: 'Plan',
    title: 'Ready-to-Teach Lesson System',
    subtitle: 'Professional Lesson Plans, Class Structures & Instructor Guides',
    contents:
      'Lesson frameworks, class timing, demonstrations, guided practice, complete lesson plans, workshops and multi-session course structures.',
    benefit: 'Reduce lesson preparation and walk into class with a clear teaching plan.',
    crop: { x: 548, y: 143, w: 212, h: 330 },
  },
  {
    number: '04',
    stage: 'Practice',
    title: 'Student Practice & Skills Workbook',
    subtitle: 'Hands-On Practice, Skill Development & Progress Tracking',
    contents:
      'Face mapping, foundation matching, complexion labs, color theory, brows, eyes, eyeliner, lips, complete-look planning, client briefs, troubleshooting, timed challenges and progress tracking.',
    benefit: 'Turn passive students into active learners through deliberate practice.',
    crop: { x: 776, y: 146, w: 212, h: 330 },
  },
  {
    number: '05',
    stage: 'Assess',
    title: 'Assessment & Feedback System',
    subtitle: 'Measure Skills, Track Progress & Build Student Confidence',
    contents:
      'Diagnostic assessments, quizzes, technique rubrics, practical evaluations, feedback frameworks, reassessment and final readiness.',
    benefit: 'Replace vague feedback with structured, documented evaluation.',
    crop: { x: 1003, y: 148, w: 212, h: 330 },
  },
  {
    number: '06',
    stage: 'Manage',
    title: 'Professional Instructor & Classroom System',
    subtitle: 'Organize, Manage & Run Makeup Education Professionally',
    contents:
      'Course planning, instructor dashboards, student records, attendance, classroom setup, inventory, hygiene systems, communication and course completion.',
    benefit: 'Operate your training with the structure of a professional makeup academy.',
    crop: { x: 1232, y: 153, w: 212, h: 330 },
  },
]

/** Section 6 — real Volume 4 pages, in file-name order (01 → 06). */
export const galleryPages = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/mpdcw1G2maVrrwgFbRN68x/01-shadow-map-5-look-types.webp',
    title: 'Shadow Map Practice — 5 Look Types',
    verb: 'Plan it.',
    caption: 'Visual mapping before application.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/gtVEXtP8gtk6rn49Qa46e3/02-troubleshooting-13-problems.webp',
    title: 'Troubleshooting Workout — 13 Problems',
    verb: 'Correct it.',
    caption: 'Troubleshooting frameworks for real mistakes.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/o5VnSquF3UUN5nN2r1Ukjn/03-face-mapping-contour-bronzer-blush-highlight.webp',
    title: 'Contour, Bronzer, Blush & Highlight Mapping',
    verb: 'Practice it.',
    caption: 'Structured exercises instead of random repetition.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/bf1VoJ8nzuZ7FujKJaFh1X/04-practice-log-evidence-progress.webp',
    title: 'Practice Log & Evidence of Progress',
    verb: 'Track it.',
    caption: 'Document practice, feedback and improvement.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/czXZmsoqoHoB5rb9gY2dpw/05-day-to-evening-transformation.webp',
    title: 'Look Planner — Day to Evening Transformation',
    verb: 'Challenge it.',
    caption: 'Client-style briefs that build real-world thinking.',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/vRTpuvpzxW3bFCTJXpqwdN/06-final-readiness-audit-15-skills.webp',
    title: 'Final Readiness Audit — 15 Skill Areas',
    verb: 'Assess it.',
    caption: 'Know exactly where a student needs development.',
  },
]

/** Section 7 — use cases. */
export const useCases = [
  { icon: UserIcon, title: '1:1 Makeup Training', description: 'Create structured private lessons.' },
  { icon: UsersIcon, title: 'Group Classes', description: 'Keep students working toward clear objectives.' },
  { icon: GraduationCapIcon, title: 'Makeup Academies', description: 'Standardize teaching resources and student progression.' },
  { icon: PresentationIcon, title: 'Workshops', description: 'Build focused, professional learning experiences.' },
  { icon: SparklesIcon, title: 'Masterclasses', description: 'Support demonstrations with structured materials.' },
  { icon: BriefcaseIcon, title: 'Professional MUA Training', description: 'Transform practical experience into teachable systems.' },
]

/** Section 8 — the five extra bonuses. */
export const bonuses = [
  {
    number: 'Bonus #1',
    title: 'The Ultimate Makeup Face Chart Library',
    description: 'Professional face charts, mapping templates and look-planning worksheets.',
    crop: { x: 392, y: 490, w: 150, h: 225 },
  },
  {
    number: 'Bonus #2',
    title: '50 Ready-to-Use Makeup Class Activities',
    description: 'Engaging exercises, challenges and skill-building activities.',
    crop: { x: 553, y: 490, w: 150, h: 225 },
  },
  {
    number: 'Bonus #3',
    title: 'The Makeup Instructor Quick Reference Guide',
    description: 'Visual cheat sheets, technique references and professional classroom reference cards.',
    crop: { x: 714, y: 490, w: 150, h: 225 },
  },
  {
    number: 'Bonus #4',
    title: '50 Ready-to-Teach Makeup Lesson Ideas',
    description: 'A complete lesson idea library for makeup instructors and educators.',
    crop: { x: 867, y: 490, w: 150, h: 225 },
  },
  {
    number: 'Bonus #5',
    title: 'The Professional Makeup Instructor Printable Pack',
    description: 'Essential checklists, student records, progress trackers, assessment forms and classroom documents.',
    crop: { x: 1021, y: 492, w: 150, h: 225 },
  },
]

/** Section 9 — everything included. */
export const coreIncludes = [
  '6 Core Professional Teaching Volumes',
  'Step-by-Step Technique Resources',
  'Ready-to-Teach Lesson Structures',
  'Student Practice System',
  'Assessment & Feedback Tools',
  'Instructor & Classroom Management',
]

export const bonusIncludes = [
  'Professional Face Chart Library',
  '50 Class Activities',
  'Quick Reference Guide',
  '50 Lesson Ideas',
  'Professional Printable Pack',
]

/** Section 10 — who it is for. */
export const audience = [
  'Makeup Instructor',
  'Makeup Educator',
  'Beauty School Teacher',
  'Makeup Academy Owner',
  'Independent Makeup Trainer',
  'Professional MUA Who Teaches',
  'Workshop or Masterclass Educator',
  'MUA preparing to structure educational services',
]

/** Section 11 — the student journey. */
export const journey = [
  { stage: 'Know', verb: 'Understand.' },
  { stage: 'Teach', verb: 'Demonstrate.' },
  { stage: 'Plan', verb: 'Structure.' },
  { stage: 'Practice', verb: 'Develop skills.' },
  { stage: 'Assess', verb: 'Measure progress.' },
  { stage: 'Manage', verb: 'Run education professionally.' },
]

/** Section 12 — using it in class. */
export const experienceSteps = [
  { number: '01', title: 'Select', description: 'Choose the lesson, technique, exercise or resource.' },
  { number: '02', title: 'Prepare', description: 'Use the instructor framework and required materials.' },
  { number: '03', title: 'Teach', description: 'Demonstrate with structured visual support.' },
  { number: '04', title: 'Practice & Track', description: 'Guide student practice, feedback and improvement.' },
]

export const faqs = [
  {
    question: 'Is this a makeup course?',
    answer:
      'No. It is a professional teaching and education resource system designed for makeup instructors, educators and MUAs who teach.',
  },
  {
    question: 'Is this only for established instructors?',
    answer:
      'No. It can support experienced educators as well as professional MUAs beginning to structure their teaching.',
  },
  {
    question: 'What do I receive?',
    answer: 'Six core professional volumes plus five additional bonus resources in digital format.',
  },
  {
    question: 'Can I print the resources?',
    answer: 'The system includes print-ready resources designed for practical educational use.',
  },
  {
    question: 'Are the files editable?',
    answer: 'No. The final resources are delivered as finished, non-editable PDFs.',
  },
  {
    question: 'Can I use this for private lessons and group classes?',
    answer:
      'Yes. The resources cover one-to-one training, classes, workshops and other makeup education formats.',
  },
  {
    question: 'Does this provide an official teaching license or accreditation?',
    answer:
      'No. It is an educational resource system and does not represent government licensing or external accreditation.',
  },
  {
    question: 'How much does it cost?',
    answer: 'US$27 as a one-time payment.',
  },
]
